import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { ui } from '@procuros/datachecks';

import { logError } from 'services/logging/logging';
import { DEFAULT_CURRENCY, isValidCurrency } from 'support/helpers/currency/currency';
import { GenericError } from 'support/helpers/errors/errors';
import { createJsonata } from 'support/helpers/jsonata/jsonata';
import { ColumnTypesInternal } from 'support/types/webediTable';

/**
 * Ensures a caught value is a proper Error instance before passing it to logError.
 * Libraries like JSONata throw plain objects (not Error instances), which Sentry
 * cannot meaningfully group when captured via captureException.
 *
 * When the value is a plain object (e.g. a JSONata error with code, position, token),
 * those extra properties are preserved as GenericError.otherInformation so logError
 * can forward them to Sentry as extras for diagnostic context.
 */
export const toError = (value: unknown): Error => {
  if (value instanceof Error) {
    return value;
  }
  const message =
    value !== null && typeof value === 'object' && 'message' in value
      ? String((value as { message: unknown }).message)
      : String(value);
  return new GenericError(message, value);
};

export function getAvailableColumns(config: object): Map<string, boolean> {
  const availableColumns = new Map<string, boolean>();

  function addKeys(obj: Record<string, unknown>, parentKey = ''): void {
    if (typeof obj !== 'object' || obj === null) {
      return;
    }
    for (const key of Object.keys(obj)) {
      const fullPath = parentKey ? `${parentKey}.${key}` : key;
      if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        addKeys(obj[key] as any, fullPath);
      } else {
        availableColumns.set(fullPath, true);
      }
    }
  }

  addKeys(config as Record<string, unknown>);

  return availableColumns;
}

export const usePinnedColumns = ({
  config,
  numberOfPinnedColumns,
  selectableRows,
  deletableRows,
  withIndexColumn,
}: {
  config: Array<ui.Field>;
  numberOfPinnedColumns: number | undefined;
  selectableRows?: boolean;
  deletableRows?: boolean;
  withIndexColumn?: boolean;
}) => {
  const pinnedColumns = useMemo(() => {
    const pinnedIds: Array<string> = [];
    if (numberOfPinnedColumns && numberOfPinnedColumns > 0) {
      if (selectableRows) {
        pinnedIds.push(ColumnTypesInternal.procuros_select);
      }
      if (deletableRows) {
        pinnedIds.push(ColumnTypesInternal.procuros_delete);
      }
      if (withIndexColumn) {
        pinnedIds.push(ColumnTypesInternal.procuros_index);
      }
    }

    if (!numberOfPinnedColumns) {
      return pinnedIds;
    }
    for (let i = 0; i < numberOfPinnedColumns && i < config.length; i++) {
      const path = config[i].path;
      if (path) {
        pinnedIds.push(path);
      }
    }

    return pinnedIds;
  }, [config, deletableRows, numberOfPinnedColumns, selectableRows, withIndexColumn]);

  return pinnedColumns;
};

export const useEnumerableOptions = (config: ui.EnumerableField['type_config'] | undefined) => {
  const isStaticOptions = config && 'options' in config;
  const [options, setOptions] = useState<ui.EnumerableFieldTypeConfigWithValues['options']>(
    isStaticOptions ? config.options : [],
  );
  const [hasResolved, setHasResolved] = useState(Boolean(isStaticOptions));
  const [section, field] = useMemo(() => {
    const parts = config && 'options_lookup_field' in config ? config.options_lookup_field.split('.') : [''];
    return [parts[0], parts.slice(1).join('.')];
  }, [config]);

  const jsonataExpression = useMemo(() => {
    //the expression is used so we capture undefined values
    return field && createJsonata(`$map($$, function ($v) {$exists($v.${field}) ? $v.${field} : ""})`);
  }, [field]);

  const data = useWatch({ name: section, disabled: !section });

  useEffect(() => {
    if (config && 'options' in config) {
      setOptions(config.options);
      setHasResolved(true);
    }
  }, [config]);

  useEffect(() => {
    if (!config) {
      return;
    }
    if (jsonataExpression && !('options' in config)) {
      jsonataExpression
        .evaluate(data)
        .then((options) => {
          const optionsAsArray = Array.isArray(options) ? options : [options];
          const newOptions = optionsAsArray.map((option: string, index) => {
            const formattedIndex = new Intl.NumberFormat(navigator.language, { minimumIntegerDigits: 3 }).format(
              index + 1,
            );

            return {
              label: option ? option : String(formattedIndex),
              value: `$['${section}'][${index}]`,
            };
          });

          setOptions(newOptions);
          setHasResolved(true);
        })
        .catch((e) => logError(toError(e)));
    }
  }, [config, data, jsonataExpression, section]);

  return { options: useDeferredValue(options), hasResolved };
};

export const useValue = (data: any, fieldId: string, expression: string | undefined) => {
  const [value, setValue] = useState();
  const inputValue = useWatch({ name: fieldId, disabled: Boolean(expression) });
  const jsonataExpression = useMemo(() => {
    return expression && createJsonata(expression);
  }, [expression]);

  useEffect(() => {
    if (!jsonataExpression) {
      return;
    }

    jsonataExpression
      .evaluate(data)
      .then((newValue: any) => {
        if (newValue !== value) {
          setValue(newValue);
        }
      })
      .catch((e) => logError(toError(e)));
  }, [jsonataExpression, inputValue, data, fieldId, value]);

  //we need to return the inputValue right away if there is no expression otherwise https://stackoverflow.com/questions/76366010/react-input-caret-jumps-to-end-if-value-gets-modified
  return jsonataExpression ? value : inputValue;
};

export const useDocumentCurrency = ({
  disabled,
  name = 'info.0.currency',
}: { disabled?: boolean; name?: string } = {}) => {
  const currency = useWatch({ name, disabled });
  return isValidCurrency(currency) ? currency : DEFAULT_CURRENCY;
};
